import { useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useSendMassEmailApiV1SendMassEmailPost } from '#/shared/api/generated/communication/theCargoCommunication'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import {
  activeMassJobsQueryKey,
  useMassEmailPreview,
} from '#/shared/lib/mass-jobs'
import {
  expandTemplateChips,
  htmlToPlainText,
  toEmailBodyHtml,
} from '#/shared/lib/html'
import type { MassRemovedRecipient } from '#/shared/ui/mass-recipients'
import type { NoteEditorController } from '#/shared/ui/note-editor'
import { toast } from '#/shared/ui/sonner'

import type { MassEmailViewModel } from '../lib/mass-email'

export interface UseMassEmailResult {
  message: string
  setMessage: (html: string) => void
  subject: string
  setSubject: (value: string) => void
  expandedRemoved: boolean
  toggleRemoved: () => void
  fromEmail: string
  setFromEmail: (value: string) => void
  editorRef: React.Ref<NoteEditorController>
  insertField: (content: string) => void
  totalSelected: number
  willReceive: number
  removed: MassRemovedRecipient[]
  removedCount: number
  hasMessage: boolean
  isSending: boolean
  send: () => Promise<boolean>
}

export function useMassEmail(
  viewModel: MassEmailViewModel,
  // Externally-owned drafts (the dialog hoists them per workspace tab so a
  // tab switch that unmounts the body doesn't lose the typed content).
  drafts?: {
    message: [string, (value: string) => void]
    subject: [string, (value: string) => void]
  },
): UseMassEmailResult {
  const { t } = useTranslation()
  const localMessage = useState('')
  const localSubject = useState('')
  const [message, setMessage] = drafts?.message ?? localMessage
  const [subject, setSubject] = drafts?.subject ?? localSubject
  const [expandedRemoved, setExpandedRemoved] = useState(false)
  const [fromEmail, setFromEmail] = useState(viewModel.fromEmails[0] ?? '')
  const editorRef = useRef<NoteEditorController>(null)
  const queryClient = useQueryClient()
  const sendMutation = useSendMassEmailApiV1SendMassEmailPost()

  const plainText = useMemo(
    () => htmlToPlainText(expandTemplateChips(message)),
    [message],
  )
  const preview = useMassEmailPreview(viewModel.recipients)
  const willReceive = preview.recipientCount
  const hasMessage = plainText.length > 0

  const send = async (): Promise<boolean> => {
    if (willReceive === 0 || !hasMessage) return false
    try {
      await sendMutation.mutateAsync({
        data: {
          recipients: viewModel.recipients,
          from_email: fromEmail || null,
          subject: subject.trim() || null,
          body: toEmailBodyHtml(expandTemplateChips(message)),
        },
      })
      await queryClient.invalidateQueries({
        queryKey: activeMassJobsQueryKey(),
      })
      return true
    } catch (error) {
      toast.error(getApiErrorDetail(error) ?? t('massEmail.toast.error'))
      return false
    }
  }

  return {
    message,
    setMessage,
    subject,
    setSubject,
    expandedRemoved,
    toggleRemoved: () => setExpandedRemoved((prev) => !prev),
    fromEmail,
    setFromEmail,
    editorRef,
    insertField: (content) => {
      editorRef.current?.insertContent(content)
      editorRef.current?.focus()
    },
    totalSelected: preview.totalSelected,
    willReceive,
    removed: preview.removed,
    removedCount: preview.removedCount,
    hasMessage,
    isSending: sendMutation.isPending,
    send,
  }
}
